import { useMemo } from 'react';

import { FormControl, FormGroup, FormHelperText } from '@mui/material';
import type { Control, FieldError, FieldValues } from 'react-hook-form';

import { RaCheckbox } from '@modules/_shared/components/form/RaCheckbox';
import type { CheckboxFieldDefinition } from '@modules/_shared/components/form/RaForm';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';

interface RaCheckboxGroupProps<TFormData extends FieldValues = FieldValues> {
  control: Control<any>;
  label: string;
  checkboxes: (Omit<CheckboxFieldDefinition<TFormData>, 'disabled'> & {
    disabled?: boolean;
  })[];
  formValues?: any;
  disabled?: boolean;
  row?: boolean;
}

export const RaCheckboxGroup = <TFormData extends FieldValues = FieldValues>({
  control,
  label,
  checkboxes,
  formValues = {},
  disabled,
  row,
}: RaCheckboxGroupProps<TFormData>) => {
  const checkboxErrors = useMemo(
    () =>
      checkboxes
        .map(checkbox => control.getFieldState(checkbox.name).error)
        .filter((error): error is FieldError => error != null),
    [checkboxes, control],
  );

  return (
    <FormControl
      fullWidth
      component="fieldset"
      variant="standard"
      error={checkboxErrors.length > 0}
    >
      <RaLabel label={label} component="legend" />
      <FormGroup row={row}>
        {checkboxes.map(checkbox => {
          if (checkbox.render && !checkbox.render(formValues)) {
            return null;
          }
          return (
            <RaCheckbox
              key={checkbox.name}
              control={control}
              name={checkbox.name}
              label={checkbox.label}
              disabled={disabled || checkbox.disabled}
              inFormGroup
              tooltip={
                typeof checkbox.tooltip === 'function'
                  ? checkbox.tooltip(formValues)
                  : checkbox.tooltip
              }
            />
          );
        })}
      </FormGroup>
      <FormHelperText>
        {checkboxErrors.length > 0
          ? checkboxErrors.map(error => error.message).join(', ')
          : null}
      </FormHelperText>
    </FormControl>
  );
};
